import {
  estimateSdk, getGetCardQueryOptions, getGetEventsQueryOptions, getGetEventQueryOptions,
  getGetFaceAttributeSamplesQueryOptions, getGetFaceAttributesQueryOptions,
  getGetGroupsQueryOptions, getGetHandlerQueryOptions, getGetHandlersQueryOptions,
  getGetListsQueryOptions, getGetStreamQueryOptions, getGetStreamsQueryOptions, getSample,
} from '@vlabs/api/artemy/client';
import type { GetEventsParams } from '@vlabs/api/artemy/model';
import { getArtemySampleUrl } from '@vlabs/api/axios/point2-client';
import { queryClient } from '@vlabs/api/queryClient';
import i18n from 'i18next';

import { EVENT_TARGET } from './constants';
import type {
  CardView, EventApi, EventDetectionItemView, EventOption, EventOptionItem,
  EventView, SampleView, StreamGroupView,
} from './EventApi';

const enrichDetections = (
  detections: { sample_id?: string | null; image_origin?: string | null }[] | null | undefined,
  kind: 'faces' | 'bodies',
): EventDetectionItemView[] => (detections ?? []).map((d) => ({
  ...d,
  sample_id: d.sample_id,
  image_origin: d.image_origin,
  uiSampleURL: d.sample_id ? getArtemySampleUrl(kind, d.sample_id) : undefined,
}));

const toEventView = (event: Record<string, unknown>): EventView => ({
  ...event,
  event_id: event.event_id as string | undefined,
  create_time: event.create_time as string | undefined,
  handler_id: event.handler_id as string | undefined,
  stream_id: event.stream_id as string | undefined,
  source: event.source as string | null | undefined,
  location: event.location as EventView['location'],
  tags: event.tags as string[] | null | undefined,
  track_id: event.track_id as string | null | undefined,
  top_match: event.top_match as Record<string, unknown> | null | undefined,
  face_detections: enrichDetections(event.face_detections as never, 'faces'),
  body_detections: enrichDetections(event.body_detections as never, 'bodies'),
  attach_result: event.attach_result as string[] | undefined,
  user_data: event.user_data as string | undefined,
});

const toOptions = (
  items: EventOptionItem[],
  valueKey: 'name' | 'handler_id' | 'list_id',
  labelKey: 'name' | 'description' | 'user_data',
): EventOption[] => items.map((i) => ({
  value: (i[valueKey] ?? i.stream_id ?? ''),
  label: (i[labelKey] ?? i[valueKey] ?? i.stream_id ?? ''),
}));

const toCardView = (card: Record<string, unknown>): CardView => ({
  card_id: (card.card_id ?? card.external_id) as string | undefined,
  external_id: (card.external_id ?? card.card_id) as string | undefined,
  user_data: card.user_data as CardView['user_data'],
});

const toSamples = (
  samples: (string | null | undefined)[] | undefined,
): { samples: SampleView[] } => ({
  samples: (samples ?? []).map((sampleId) => (sampleId ? {
    sample_id: sampleId,
    uiSampleURL: getArtemySampleUrl('faces', sampleId),
  } : {})),
});

const buildStreamGroups = (
  groups: { group_id?: string; group_name?: string }[],
  streams: { stream_id?: string; name?: string; groups?: string[] }[],
): StreamGroupView[] => {
  const ungrouped: { name: string }[] = [];
  const byGroupName = new Map<string, { name: string }[]>();

  streams.forEach((s) => {
    const item = { name: s.name ?? s.stream_id ?? '' };
    const groupNames = s.groups ?? [];

    if (groupNames.length === 0) {
      ungrouped.push(item);
      return;
    }

    groupNames.forEach((groupName) => {
      const groupStreams = byGroupName.get(groupName);

      if (groupStreams) {
        groupStreams.push(item);
      } else {
        byGroupName.set(groupName, [item]);
      }
    });
  });

  const views = groups.map((g) => ({
    id: g.group_id ?? '',
    label: g.group_name ?? '',
    items: (byGroupName.get(g.group_name ?? '') ?? []).map((s) => ({
      value: s.name,
      label: s.name,
    })),
  }));

  if (ungrouped.length > 0) {
    views.push({
      id: '__ungrouped__',
      label: i18n.t('sources.ungrouped sources title'),
      items: ungrouped.map((s) => ({ value: s.name, label: s.name })),
    });
  }

  return views.filter((g) => g.items.length > 0);
};

export const artemyEventApi: EventApi = {
  getEvents: async (params) => {
    const data = await queryClient.fetchQuery(getGetEventsQueryOptions({
      ...params,
      target: EVENT_TARGET,
    } as GetEventsParams));
    return { events: (data?.events ?? []).map((e) => toEventView(e as unknown as Record<string, unknown>)) };
  },
  getEvent: async (eventId) => {
    const event = await queryClient.fetchQuery(getGetEventQueryOptions(eventId, {
      target: EVENT_TARGET,
    }));
    return toEventView(event);
  },
  getStream: async (streamId) => {
    const stream = await queryClient.fetchQuery(getGetStreamQueryOptions(streamId));
    if (!stream) return undefined;
    return { stream_id: stream.stream_id ?? streamId, name: stream.name };
  },
  getStreamOptions: async () => {
    const data = await queryClient.fetchQuery(getGetStreamsQueryOptions());
    return toOptions(data?.streams ?? [], 'name', 'name');
  },
  getStreamGroups: async () => {
    const [groupsData, streamsData] = await Promise.all([
      queryClient.fetchQuery(getGetGroupsQueryOptions()),
      queryClient.fetchQuery(getGetStreamsQueryOptions()),
    ]);
    return buildStreamGroups(groupsData?.groups ?? [], streamsData?.streams ?? []);
  },
  getHandlerOptions: async () => {
    const data = await queryClient.fetchQuery(getGetHandlersQueryOptions());
    return toOptions(data?.handlers ?? [], 'handler_id', 'description');
  },
  getHandler: async (handlerId) => {
    const handler = await queryClient.fetchQuery(getGetHandlerQueryOptions(handlerId));
    return (handler ?? undefined);
  },
  getListOptions: async () => {
    const data = await queryClient.fetchQuery(getGetListsQueryOptions());
    return toOptions(data?.lists ?? [], 'list_id', 'user_data');
  },
  getCard: async (cardId) => {
    const card = await queryClient.fetchQuery(getGetCardQueryOptions(cardId, { targets: ['user_data'] }));
    return toCardView(card as unknown as Record<string, unknown>);
  },
  getFaceAttributeSamples: async (faceId) => {
    const data = await queryClient.fetchQuery(getGetFaceAttributeSamplesQueryOptions(faceId));
    return toSamples(data?.samples);
  },
  getFaceAttributes: async (faceId) => {
    const face = await queryClient.fetchQuery(getGetFaceAttributesQueryOptions(faceId));
    return (face?.attributes?.basic_attributes ?? undefined);
  },
  getSample: async (kind, sampleId) => getSample(kind, sampleId),
  estimateSdk: async (file, params) => {
    const data = await estimateSdk(file, params, { headers: { 'Content-Type': file.type } });
    return { data: data as Record<string, unknown> };
  },
  capabilities: {
    externalIdField: 'card_ids',
    topSimilarCardIds: true,
    videoArchive: false,
  },
};

export { enrichDetections as enrichArtemyDetections, toEventView as toArtemyEventView };
